import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

export interface AuditLogEntry {
  user_id: string;
  company_id: string;
  table_name: string;
  operation: string;
  record_id?: string;
  old_values?: Record<string, any>;
  new_values?: Record<string, any>;
  details?: Record<string, any>;
  severity?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  ip_address?: string;
  user_agent?: string;
}

export const useAuditLog = () => {
  const [isLogging, setIsLogging] = useState(false);
  const { user, selectedCompany } = useAuth();
  const { toast } = useToast();

  const logAction = useCallback(async (entry: Omit<AuditLogEntry, 'user_id' | 'company_id'>) => {
    if (!user || !selectedCompany) {
      console.warn('Cannot log action: user or company not selected');
      return;
    }

    setIsLogging(true);

    try {
      const auditEntry: AuditLogEntry = {
        user_id: user.id,
        company_id: selectedCompany.id,
        severity: 'MEDIUM',
        ...entry,
        details: {
          ...entry.details,
          timestamp: new Date().toISOString(),
          user_email: user.email,
          company_name: selectedCompany.name,
          // Detectar información del navegador
          user_agent: navigator.userAgent,
        }
      };

      // Usar RPC para insertar en audit_log de forma segura
      const { error } = await supabase.rpc('insert_audit_log_secure', {
        p_table_name: auditEntry.table_name,
        p_operation: auditEntry.operation,
        p_record_id: auditEntry.record_id || null,
        p_old_values: auditEntry.old_values || null,
        p_new_values: auditEntry.new_values || null,
        p_details: auditEntry.details || null,
        p_severity: auditEntry.severity || 'INFO'
      });

      if (error) {
        console.error('Error logging action:', error);
        // No mostrar toast de error por defecto para no interrumpir UX
      }
    } catch (error) {
      console.error('Unexpected error logging action:', error);
    } finally {
      setIsLogging(false);
    }
  }, [user, selectedCompany]);

  // Log específico para cambio de empresa con control de duplicados
  const logCompanySwitch = useCallback(async (
    fromCompany: { id: string; name: string } | null,
    toCompany: { id: string; name: string }
  ) => {
    // Evitar logs duplicados
    if (fromCompany?.id === toCompany.id) {
      console.log('🚫 Evitando log duplicado - misma empresa:', toCompany.name);
      return;
    }

    // Implementar throttling - solo un log por minuto por usuario
    const throttleKey = `company_switch_${user?.id}`;
    const lastLog = localStorage.getItem(throttleKey);
    const now = Date.now();
    
    if (lastLog && (now - parseInt(lastLog)) < 60000) { // 1 minuto
      console.log('🕐 Log de cambio de empresa throttled - muy frecuente');
      return;
    }
    
    localStorage.setItem(throttleKey, now.toString());

    await logAction({
      table_name: 'company_context',
      operation: 'COMPANY_SWITCH',
      details: {
        from_company_id: fromCompany?.id,
        from_company_name: fromCompany?.name,
        to_company_id: toCompany.id,
        to_company_name: toCompany.name,
        timestamp: new Date().toISOString()
      },
      severity: 'LOW' // Cambiar a LOW ya que es una operación normal
    });
  }, [logAction, user?.id]);

  // Log para acciones críticas
  const logCriticalAction = useCallback(async (
    operation: string,
    tableName: string,
    recordId?: string,
    details?: Record<string, any>
  ) => {
    await logAction({
      table_name: tableName,
      operation,
      record_id: recordId,
      details,
      severity: 'CRITICAL'
    });
  }, [logAction]);

  return {
    logAction,
    logCompanySwitch,
    logCriticalAction,
    isLogging
  };
};